export interface ServerConfig {
  turnstileSiteKey: string | null;
  turnstileRequired: boolean;
  vapidPublicKey: string | null;
  pushEnabled: boolean;
  chunkSize: number;
  sessionTtlSeconds: number;
  maxUploadBytes: number;
}

export interface QuarantineItem {
  id: string;
  name: string;
  relativePath: string;
  size: number;
  mime: string;
  sha256: string | null;
  scanned: boolean;
  scanResult: string | null;
  hasThumbnail: boolean;
  createdAt: number;
  transferId: string;
  origin: {
    ip: string | null;
    city: string | null;
    country: string | null;
    userAgent: string | null;
  };
}

export interface LibraryFile {
  id: string;
  name: string;
  relativePath: string;
  size: number;
  mime: string;
  sha256: string | null;
  hasThumbnail: boolean;
  createdAt: number;
  keptAt: number | null;
}

export interface DeviceSummary {
  id: string;
  name: string;
  kind: 'ios' | 'macos' | 'browser';
  createdAt: number;
  lastSeen: number | null;
  hasPush: boolean;
  current: boolean;
}

export interface Stats {
  pending: number;
  quarantineBytes: number;
  libraryBytes: number;
  ceilingBytes: number;
  freeBytes: number;
  ttlHours: number;
  libraryTtlDays: number;
  pushEnabled: boolean;
  fileCount: number;
}

const TOKEN_KEY = 'drop.token';
const NAME_KEY = 'drop.device';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null, name?: string): void {
  try {
    if (token === null) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(NAME_KEY);
    } else {
      localStorage.setItem(TOKEN_KEY, token);
      if (name) localStorage.setItem(NAME_KEY, name);
    }
  } catch {
    // private browsing; the session simply will not persist
  }
}

export function getDeviceName(): string | null {
  try {
    return localStorage.getItem(NAME_KEY);
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init: RequestInit = {}, auth = true): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (auth) {
    const token = getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(path, { ...init, headers });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body: unknown = text ? safeParse(text) : null;

  if (!res.ok) {
    const err = body as { error?: string; message?: string } | null;
    throw new ApiError(
      res.status,
      err?.error ?? 'request_failed',
      err?.message ?? err?.error ?? `Request failed (${res.status})`,
    );
  }
  return body as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  config: () => request<ServerConfig>('/api/config', {}, false),

  createSession: (turnstileToken?: string) =>
    request<{ sessionId: string; code: string; expiresAt: number }>(
      '/api/session',
      { method: 'POST', body: JSON.stringify({ turnstileToken }) },
      false,
    ),

  pair: (code: string, name: string, kind: 'browser' | 'ios' | 'macos' = 'browser') =>
    request<{ token: string; device: { id: string; name: string; kind: string } }>(
      '/api/devices/pair',
      { method: 'POST', body: JSON.stringify({ code, name, kind }) },
      false,
    ),

  quarantine: () =>
    request<{
      items: QuarantineItem[];
      totalBytes: number;
      ceilingBytes: number;
      ttlHours: number;
      libraryTtlDays: number;
    }>('/api/quarantine'),

  keep: (id: string) =>
    request<{ ok: boolean; pending: number }>(`/api/quarantine/${id}/keep`, { method: 'POST' }),

  discard: (id: string) =>
    request<{ ok: boolean; pending: number }>(`/api/quarantine/${id}/discard`, { method: 'POST' }),

  quarantineBulk: (ids: string[], action: 'keep' | 'discard') =>
    request<{ ok: boolean; count: number; pending: number }>('/api/quarantine/bulk', {
      method: 'POST',
      body: JSON.stringify({ ids, action }),
    }),

  files: (params: { cursor?: string | null; q?: string; kind?: string; limit?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.cursor) search.set('cursor', params.cursor);
    if (params.q) search.set('q', params.q);
    if (params.kind) search.set('kind', params.kind);
    if (params.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return request<{ items: LibraryFile[]; nextCursor: string | null; totalBytes: number }>(
      `/api/files${qs ? `?${qs}` : ''}`,
    );
  },

  archive: (ids: string[], name?: string) =>
    request<{ url: string; token: string; count: number }>('/api/files/archive', {
      method: 'POST',
      body: JSON.stringify({ ids, name }),
    }),

  deleteFiles: (ids: string[]) =>
    request<{ ok: boolean; count: number }>('/api/files/delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  push: (code: string, ids: string[]) =>
    request<{ ok: boolean; token: string; count: number; expiresAt: number }>(
      `/api/push/${encodeURIComponent(code)}`,
      { method: 'POST', body: JSON.stringify({ ids }) },
    ),

  devices: () => request<{ devices: DeviceSummary[] }>('/api/devices'),

  pairingCode: () =>
    request<{ code: string; expiresAt: number }>('/api/devices/pairing-code', { method: 'POST' }),

  revoke: (id: string) => request<{ ok: boolean }>(`/api/devices/${id}`, { method: 'DELETE' }),

  saveSubscription: (subscription: PushSubscriptionJSON | null) =>
    request<{ ok: boolean; pushEnabled: boolean }>('/api/devices/subscription', {
      method: 'POST',
      body: JSON.stringify({ subscription }),
    }),

  stats: () => request<Stats>('/api/stats'),

  setTtl: (settings: { ttlHours?: number; libraryTtlDays?: number }) =>
    request<{ ok: boolean; ttlHours: number; libraryTtlDays: number }>('/api/settings', {
      method: 'POST',
      body: JSON.stringify(settings),
    }),
};

/** Authenticated thumbnails need a fetch, not an <img src>. */
export async function fetchThumbnail(id: string): Promise<string | null> {
  const token = getToken();
  if (!token) return null;
  try {
    const res = await fetch(`/api/files/${id}/thumb`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  } catch {
    return null;
  }
}

/** Download through fetch so the Authorization header travels with it. */
export async function downloadFile(id: string, filename: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`/api/files/${id}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, 'download_failed', 'Download failed');
  const url = URL.createObjectURL(await res.blob());
  triggerDownload(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function triggerDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
